/**
 * Z-Index Constants
 *
 * This file defines all z-index values used throughout the application.
 * Values are organized by purpose and layer to ensure proper stacking order.
 *
 * Usage:
 * import { Z_INDEX } from 'common/constants/zIndex'
 *
 * // In styles
 * zIndex: Z_INDEX.BACKGROUND
 *
 * // In CSS classes
 * className="z-20" // Use Tailwind classes for common values
 */

export const Z_INDEX = {
  // Base layers (0-10)
  BACKGROUND: 0,
  CONTENT: 1,
  OVERLAY: 5,
  ELEVATED: 10,

  // UI Components (10-100)
  HEADER: 20,
  SIDEBAR: 30,
  CARD: 50,
  DROPDOWN: 100,

  // Interactive Elements (100-1000)
  TOOLTIP: 1000,
  FLOATING_ACTION: 1000,
  NOTIFICATION: 1100,
  DRAWER: 1300,

  // Modal and Overlay Layers (1000+)
  MODAL_BACKDROP: 1400,
  MODAL: 1500,
  DIALOG: 1600,

  // Dropdown and Select Menus (9999+)
  // These high values ensure dropdowns appear above all other content
  DROPDOWN_MENU: 9999,
  SELECT_MENU: 99999,
  AUTOCOMPLETE_MENU: 99999,
  GLOBAL_DROPDOWN: 999999,

  // Specific component z-indexes
  CONFIGURATION_CARD: {
    BACKGROUND: 0,
    CONTENT: 1,
    SELECTOR: 99997,
    ENCODING_SELECTOR: 99998,
    DATA_MODEL_SELECTOR: 99999,
  },

  // MUI Component overrides
  MUI: {
    APP_BAR: 1,
    DRAWER: 1300,
    MODAL: 1300,
    TOOLTIP: 9999,
    SELECT_MENU: 999999,
    AUTOCOMPLETE_POPPER: 999999,
  },
} as const

/**
 * Z-Index utility functions
 */
export const Z_INDEX_UTILS = {
  /**
   * Get z-index for a specific layer
   */
  getLayer: (layer: keyof typeof Z_INDEX): number => {
    return Z_INDEX[layer] as number
  },

  /**
   * Get z-index for configuration card components
   */
  getConfigCard: (component: keyof typeof Z_INDEX.CONFIGURATION_CARD): number => {
    return Z_INDEX.CONFIGURATION_CARD[component]
  },

  /**
   * Get z-index for MUI components
   */
  getMui: (component: keyof typeof Z_INDEX.MUI): number => {
    return Z_INDEX.MUI[component]
  },

  /**
   * Check if a z-index value is above a certain layer
   */
  isAbove: (zIndex: number, layer: keyof typeof Z_INDEX): boolean => {
    return zIndex > (Z_INDEX[layer] as number)
  },

  /**
   * Check if a z-index value is below a certain layer
   */
  isBelow: (zIndex: number, layer: keyof typeof Z_INDEX): boolean => {
    return zIndex < (Z_INDEX[layer] as number)
  },
} as const

/**
 * Common z-index patterns for quick reference
 */
export const Z_INDEX_PATTERNS = {
  // Standard stacking order
  STACKING_ORDER: [
    Z_INDEX.BACKGROUND,
    Z_INDEX.CONTENT,
    Z_INDEX.OVERLAY,
    Z_INDEX.ELEVATED,
    Z_INDEX.HEADER,
    Z_INDEX.SIDEBAR,
    Z_INDEX.CARD,
    Z_INDEX.DROPDOWN,
    Z_INDEX.TOOLTIP,
    Z_INDEX.FLOATING_ACTION,
    Z_INDEX.NOTIFICATION,
    Z_INDEX.DRAWER,
    Z_INDEX.MODAL_BACKDROP,
    Z_INDEX.MODAL,
    Z_INDEX.DIALOG,
    Z_INDEX.DROPDOWN_MENU,
    Z_INDEX.SELECT_MENU,
    Z_INDEX.AUTOCOMPLETE_MENU,
    Z_INDEX.GLOBAL_DROPDOWN,
  ],

  // Layers that should appear above modals
  ABOVE_MODAL: [
    Z_INDEX.DROPDOWN_MENU,
    Z_INDEX.SELECT_MENU,
    Z_INDEX.AUTOCOMPLETE_MENU,
    Z_INDEX.GLOBAL_DROPDOWN,
  ],

  // Layers that should appear below modals
  BELOW_MODAL: [
    Z_INDEX.BACKGROUND,
    Z_INDEX.CONTENT,
    Z_INDEX.OVERLAY,
    Z_INDEX.ELEVATED,
    Z_INDEX.HEADER,
    Z_INDEX.SIDEBAR,
    Z_INDEX.CARD,
    Z_INDEX.DROPDOWN,
    Z_INDEX.TOOLTIP,
    Z_INDEX.FLOATING_ACTION,
    Z_INDEX.NOTIFICATION,
    Z_INDEX.DRAWER,
  ],
} as const

export default Z_INDEX
